import type { RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { listPublicSchools } from '@/services/setup'

export const SETUP_SETTINGS_PATH = '/admin/setup/settings'

/** Placeholder row inserted by migration 028 — not a real configured school */
export const PLACEHOLDER_SCHOOL_CODE = 'SCH001'
export const PLACEHOLDER_SCHOOL_NAME = 'My School'

export function isPlaceholderSchool(school: {
  school_name?: string
  school_code?: string
}): boolean {
  return (
    school.school_code === PLACEHOLDER_SCHOOL_CODE &&
    school.school_name === PLACEHOLDER_SCHOOL_NAME
  )
}

/** Session context school — single source of truth for super-admin bootstrap */
export function hasSessionContextSchool(): boolean {
  const authStore = useAuthStore()
  const id = authStore.user?.context_school_id
  return id !== null && id !== undefined && id !== ''
}

export function isSuperAdminWithoutContext(): boolean {
  const authStore = useAuthStore()
  return authStore.hasRole('super_admin') && !hasSessionContextSchool()
}

/**
 * Super admin with no session context:
 * - setup → create/configure first real school (Settings)
 * - select → pick among existing real schools (modal)
 */
export async function getSuperAdminContextAction(): Promise<'setup' | 'select' | null> {
  if (!isSuperAdminWithoutContext()) {
    return null
  }

  try {
    const schools = await listPublicSchools()
    const realSchools = schools.filter((s) => !isPlaceholderSchool(s))
    return realSchools.length === 0 ? 'setup' : 'select'
  } catch {
    // If we cannot load schools, send to setup rather than a blocking empty modal
    return 'setup'
  }
}

export async function getSuperAdminBootstrapRedirect(
  to: RouteLocationNormalized
): Promise<string | null> {
  if (!to.path.startsWith('/admin')) {
    return null
  }

  if (to.path.startsWith(SETUP_SETTINGS_PATH)) {
    return null
  }

  const action = await getSuperAdminContextAction()
  if (action === 'setup') {
    return `${SETUP_SETTINGS_PATH}?bootstrap=1`
  }

  return null
}

export async function shouldShowSchoolContextModal(): Promise<boolean> {
  const action = await getSuperAdminContextAction()
  return action === 'select'
}
